import React from 'react'
import { Badge, DETAIL_ACCENTS } from '../common/DetailUI';

// Read-only label/value view used by every "View" popup.
// fields: [{ label, value, badge?, full?, icon?, accent? }]
//   badge: render the value as a status pill   full: span the whole row (long text, lists)
//   icon / accent: override the icon and colour picked from the label
// route (optional): { from: { code, name, time, sub }, to: { ... }, middle } renders a compact A ✈ B strip on top.
// Each field is an open label/value pair with a colour-coded icon (no boxes); the label is small and faded,
// the value is bold and dark. Long values get a wider slot, and empty fields are collected into one "Not set" line.

const A = DETAIL_ACCENTS;
const SLATE = "#64748b";

// first match wins, so specific words (time, airport, country) come before generic ones (code, name)
const ICON_RULES = [
  [/status|active|enabled/, "bi-toggle-on", A.green],
  [/time|duration/, "bi-clock", A.amber],
  [/date|expir|valid|period/, "bi-calendar-event", A.amber],
  [/terminal|gate/, "bi-door-open", A.violet],
  [/airport/, "bi-geo-alt", A.teal],
  [/country|nationality/, "bi-globe2", A.teal],
  [/state|region/, "bi-map", A.teal],
  [/city|location|sector|route/, "bi-buildings", A.teal],
  [/pincode|zip|postal/, "bi-mailbox", A.teal],
  [/address/, "bi-house", A.teal],
  [/e-?mail/, "bi-envelope", A.sky],
  [/mobile|phone|contact|whatsapp/, "bi-telephone", A.sky],
  [/amount|balance|fare|price|markup|commission|fee|charge|credit|debit|total|tax|gst|cashback|limit|wallet/, "bi-cash-coin", A.green],
  [/airline|flight|aircraft|cabin|rbd|baggage|seat/, "bi-airplane", A.sky],
  [/bank|ifsc|account|upi|swift|micr/, "bi-bank", A.pink],
  [/role|module|menu|access|permission|read|write|delete|update/, "bi-shield-lock", A.violet],
  [/remark|message|description|note|reason|comment|body|subject/, "bi-chat-left-text", SLATE],
  [/file|document|url|link|logo|image|upload|template/, "bi-file-earmark", A.pink],
  [/created|modified|updated/, "bi-clock-history", SLATE],
  [/priority|rank|order|sequence/, "bi-sort-numeric-down", A.amber],
  [/type|category|class|mode|source/, "bi-bookmark", A.pink],
  [/user|by|agent|supplier|vendor|agency|executive|holder|person/, "bi-person", A.indigo],
  [/code|id$|number|ref|pnr|no\.?$/, "bi-hash", A.indigo],
  [/name|title/, "bi-tag", A.violet],
];

const iconFor = (label) => {
  const l = String(label || "").toLowerCase();
  const rule = ICON_RULES.find(([re]) => re.test(l));
  return rule ? { icon: rule[1], accent: rule[2] } : { icon: "bi-dot", accent: SLATE };
};

const isEmptyValue = (v) => v === undefined || v === null || v === "";
const isLong = (v) => typeof v === "string" && v.length > 38;

const labelStyle = {
  fontSize: 10.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
  color: "#64748b", opacity: 0.75, lineHeight: "14px",
};
const valueStyle = {
  fontSize: 14.5, fontWeight: 600, color: "#0f172a", lineHeight: "21px",
  marginTop: 3, wordBreak: "break-word", whiteSpace: "pre-wrap",
};

const css = `
.dv-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 18px 24px; }
.dv-wide { grid-column: span 2; }
.dv-full { grid-column: 1 / -1; }
@media (max-width: 520px) { .dv-wide { grid-column: 1 / -1; } }
`;

const RouteStrip = ({ from, to, middle }) => {
  const side = (s, align) => (
    <div style={{ textAlign: align, minWidth: 0, flex: "0 1 auto" }}>
      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1, lineHeight: 1, color: "#0f172a" }}>{s?.code || "—"}</div>
      {s?.name && <div className="text-truncate" style={{ fontSize: 12, color: "#64748b", marginTop: 4, maxWidth: 220 }}>{s.name}</div>}
      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--btn-bg)", marginTop: 4 }}>{s?.time || "—"}</div>
      {s?.sub && <div style={{ fontSize: 11, color: "#94a3b8" }}>{s.sub}</div>}
    </div>
  );
  return (
    <div
      className="d-flex align-items-center justify-content-between gap-3 mb-3"
      style={{
        background: "linear-gradient(135deg, color-mix(in srgb, var(--btn-bg) 10%, #fff) 0%, #fff 100%)",
        border: "1px solid color-mix(in srgb, var(--btn-bg) 22%, #fff)", borderRadius: 14, padding: "16px 20px",
      }}
    >
      {side(from, "left")}
      <div className="flex-grow-1 text-center" style={{ maxWidth: 300, color: "var(--btn-bg)" }}>
        <div className="d-flex align-items-center gap-2">
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "currentColor" }}></span>
          <span className="flex-grow-1" style={{ borderTop: "2px dashed currentColor", opacity: 0.45 }}></span>
          <i className="bi bi-airplane-fill" style={{ fontSize: 16, transform: "rotate(90deg)" }}></i>
          <span className="flex-grow-1" style={{ borderTop: "2px dashed currentColor", opacity: 0.45 }}></span>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "currentColor" }}></span>
        </div>
        {middle && <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6 }}>{middle}</div>}
      </div>
      {side(to, "right")}
    </div>
  );
};

const DetailView = ({ fields, route }) => {
  const filled = fields.filter((f) => !isEmptyValue(f.value));
  const empty = fields.filter((f) => isEmptyValue(f.value));

  return (
    <>
      <style>{css}</style>
      {route && <RouteStrip {...route} />}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #eef1f7", padding: "20px 22px", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)" }}>
        <div className="dv-grid">
          {filled.map((f) => {
            const auto = iconFor(f.label);
            const icon = f.icon || auto.icon;
            const accent = f.accent || auto.accent;
            const cls = f.full ? "dv-full" : isLong(f.value) ? "dv-wide" : "";
            return (
              <div key={f.label} className={"d-flex align-items-start gap-2 " + cls} style={{ minWidth: 0 }}>
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 30, height: 30, borderRadius: 9, background: accent + "1a", color: accent, fontSize: 14 }}
                >
                  <i className={"bi " + icon}></i>
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>{f.label}</div>
                  {f.badge ? (
                    <div style={{ marginTop: 4 }}><Badge value={f.value} /></div>
                  ) : (
                    <div style={valueStyle}>{f.value}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* {empty.length > 0 && (
          <div
            className="d-flex align-items-center flex-wrap gap-2"
            style={{ marginTop: filled.length ? 18 : 0, paddingTop: filled.length ? 14 : 0, borderTop: filled.length ? "1px dashed #e2e8f0" : "none" }}
          >
            <span style={{ ...labelStyle, opacity: 0.6 }}>
              <i className="bi bi-dash-circle me-1"></i>Not set
            </span>
            {empty.map((f) => (
              <span key={f.label} style={{ fontSize: 12, color: "#94a3b8", background: "#f8fafc", borderRadius: 20, padding: "2px 10px" }}>
                {f.label}
              </span>
            ))}
          </div>
        )} */}
      </div>
    </>
  );
};

export default DetailView;
